import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useLocalStorage } from "@vueuse/core";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import type { AuthorizationsByShortCode, AuthorizedFeaturesApiResponse } from "@tmi/types/api/permissions";

import userPermissionsFactory, { type UserPermissions } from "@/types/permissions";

import { AUTH_RESET_EVENT, useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// Global State
const localReferenceToPermissionsData = useLocalStorage< AuthorizationsByShortCode[] >( 'permissions', [] as unknown as AuthorizationsByShortCode[] );

const permissions = ref< UserPermissions >( userPermissionsFactory.create( localReferenceToPermissionsData.value ) );
const isInitialized = computed< boolean >( () => permissions.value.isInitialized() );
const loading = ref< boolean >( false );
const hasAccessToAnyTools = ref< boolean | null >( null );

// Self-heal bookkeeping (tmi-6gh, LD2). Module-scoped like the refs above so
// concurrent callers (router guard + Application.runInitialization) share it.
let healInFlight: Promise< boolean > | null = null;
let healCooldownUntil: number = 0;               // set after a FAILED heal; 30s
let grantsKnownEmpty: boolean = false;           // set after a SUCCESSFUL fetch returning zero grants
let lastFetchSucceededWithEmptyGrants = false;   // written by initializeAsync, read by ensureInitializedAsync
const HEAL_RETRY_COOLDOWN_MS = 30000;

function resetInMemoryState(): void {
    localReferenceToPermissionsData.value = [] as unknown as AuthorizationsByShortCode[];
    permissions.value = userPermissionsFactory.create( localReferenceToPermissionsData.value );
    grantsKnownEmpty = false;
    lastFetchSucceededWithEmptyGrants = false;
    healCooldownUntil = 0;
}

// LD1 coherence: Auth.reset() purges localStorage natively (which same-document
// VueUse refs do not observe) and then dispatches this event so the in-memory
// grants die with the token.
if ( typeof window !== 'undefined' ) {
    window.addEventListener( AUTH_RESET_EVENT, resetInMemoryState );
}

export const usePermissionsStore = defineStore( 'permissions', () => {
    const { authToken } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function reset(): void {
        // Clear out the local storage value
        localReferenceToPermissionsData.value = null;

        hasAccessToAnyTools.value = false;
        loading.value = false;

        // Reset permissions state to the original value (empty) and clear heal bookkeeping
        resetInMemoryState();
    }

    function authorizationGrants(): string {
        return JSON.stringify( localReferenceToPermissionsData.value );
    }

    async function initializeAsync(): Promise< void > {
        if ( isInitialized.value || loading.value ) {
            return;
        }

        loading.value = true;
        lastFetchSucceededWithEmptyGrants = false;

        try {
            const result =
                await goFetch< AuthorizedFeaturesApiResponse, PersistedQueryPayload >( {
                    endpoint: 'authorization/graphql',
                    payload: {
                        id: '3b0bdec2f61d30769f20a7db357f00d0',
                        variables: { userId: userStore.userId()! }
                    },
                    authToken: authToken.value
                } );

            if ( null != result?.data?.authorizedFeatureGrants ) {
                const grants = result.data.authorizedFeatureGrants.grants ?? [];

                // Load the overlay of all authorized features
                localReferenceToPermissionsData.value = [ ...grants ];

                // Parse into a minimal standard set of permissions
                initializeSynchronously();

                // UserPermissionsFactory.create( [] ) deliberately leaves isInitialized false, so a
                // legitimately zero-grant user is indistinguishable from a failed fetch without this.
                lastFetchSucceededWithEmptyGrants = grants.length === 0;

                // Set initialized based on if permissions were initialized
                if ( !isInitialized.value && !lastFetchSucceededWithEmptyGrants ) {
                    // TODO: Handle the situation where initialization finished, but had some sort of error
                    logger.error( 'Permissions were initialized, but an error exists within the logic/data' );
                }
            }
        } catch ( err ) {
            // goFetch rethrows Unauthorized/Forbidden; a failed fetch must not wedge the store.
            logger.error( `Permissions fetch failed: ${ ( err as Error )?.message ?? err }` );
        } finally {
            // Always clear the "loading" flag, otherwise the entry guard blocks every retry
            loading.value = false;
        }
    }

    /**
     * Self-heal (LD2): make sure grants are loaded, restoring the user profile and re-fetching
     * grants from the server when the local cache is empty but a token exists. Never rejects.
     * At most one attempt in flight; a failed attempt suppresses retries for 30s; a successful
     * fetch returning zero grants is memoized as satisfied.
     */
    async function ensureInitializedAsync(): Promise< boolean > {
        if ( isInitialized.value ) return true;

        initializeSynchronously();                          // cheap: localStorage
        if ( isInitialized.value ) return true;

        if ( grantsKnownEmpty ) return true;                // legit zero-grant user — do not re-heal
        if ( !authToken.value ) return false;               // nothing to heal from
        if ( Date.now() < healCooldownUntil ) return false; // recent failure — back off

        if ( !healInFlight ) {
            healInFlight = ( async () => {
                try {
                    // Lean: profile only (LD2). F44: a profile load already in flight (a sign-in's) is
                    // joined, so userId() is read after it lands rather than while it is still -1.
                    await userStore.initializeProfileAsync();
                    if ( ( userStore.userId() ?? -1 ) <= 0 ) return false;

                    await initializeAsync();                    // server grants fetch (self-catching)
                    if ( isInitialized.value ) return true;
                    if ( lastFetchSucceededWithEmptyGrants ) {
                        grantsKnownEmpty = true;
                        return true;
                    }
                    return false;
                } catch {
                    return false;                               // heal must NEVER reject (R39)
                } finally {
                    healInFlight = null;
                }
            } )().then( ok => {
                if ( !ok ) healCooldownUntil = Date.now() + HEAL_RETRY_COOLDOWN_MS;
                return ok;
            } );
        }

        return healInFlight;
    }

    /**
     * UX roadmap #50: a member who presses "Try again" on the 403 page asked for the check now. The 30 s back-off is for
     * the automatic heals of one navigation (the guard and the shell's boot), so it must not turn that retry into a
     * second instant "couldn't confirm your access".
     */
    function clearHealCooldown(): void {
        healCooldownUntil = 0;
    }

    // This is necessary for the router to be able to navigate to any link without
    // initially assuming the user doesn't have access (403).
    //
    // This should only be called from the watcher during the initialization process
    // or from the router when the user is considered to be authenticated, but the
    // permissions have not yet been initialized
    function initializeSynchronously() {
        permissions.value = userPermissionsFactory.create( localReferenceToPermissionsData.value! );
    }

    function hasAccessBasedOnAnyShortCode( shortCodes: string[] ): boolean {
        let hasAccess = false;
        shortCodes.forEach( code => hasAccess = hasAccess || hasAccessBasedOnShortCode( code ) );

        return hasAccess;
    }

    function hasAccessBasedOnShortCode( shortCode: string ): boolean {
        if ( !( shortCode && isInitialized.value ) ) {
            return false;
        }

        return permissions.value.isAuthorizedByShortCode( shortCode );
    }

    function hasFeatureAccess( feature: string ): boolean {
        return permissions.value.hasFeatureAccess( feature );
    }

    function hasArticleAccessForProduct( product: string ): boolean {
        return permissions.value.hasArticleAccessForProduct( product );
    }

    function hasBonusReportAccessForProduct( product: string ): boolean {
        return permissions.value.hasBonusReportAccessForProduct( product );
    }

    function hasCourseAccessForProduct( product: string ): boolean {
        return permissions.value.hasCourseAccessForProduct( product );
    }

    function hasModelPortfolioPermissionsForProduct( productSlug: string ): boolean {
        return permissions.value.hasModelPortfolioAccess( productSlug );
    }

    function hasWebinarAccessForProduct( product: string ): boolean {
        return permissions.value.hasWebinarAccessForProduct( product );
    }

    /******************************************************************************************************************/
    //                                          GETTERS
    /******************************************************************************************************************/

    const accessibleModelPortfoliosSlugs = computed< string[] >( () => permissions.value.getModelPortfolioSlugsForAccessibleProducts() );

    /********************************************** FEATURE PERMISSIONS ***********************************************/
    const isAllianceMember = computed< boolean >(() => permissions.value.isAllianceMember() );

    const hasArticleAccess = computed< boolean >(() => permissions.value.hasArticleAccess() );
    const hasBonusReportAccess = computed< boolean >(() => permissions.value.hasBonusReportAccess() );
    const hasCourseAccess = computed< boolean >(() => permissions.value.hasCourseAccess() );
    const hasTradeOfTheDayAccess = computed< boolean >(() => permissions.value.hasTradeOfTheDayAccess() );
    const hasStockAccess = computed< boolean >(() => permissions.value.hasStockAccess() );
    const hasEtfAccess = computed< boolean >(() => permissions.value.hasEtfAccess() );
    const hasEtfListingAccess = computed< boolean >(() => permissions.value.hasEtfListingAccess() );
    const hasEquityCalculatorAccess = computed< boolean >(() => permissions.value.hasEquityCalculatorAccess() );
    const hasMainSearchAccess = computed< boolean >(() => permissions.value.hasMainSearchAccess() );
    const hasMajorIndexBreadthAccess = computed< boolean >(() => permissions.value.hasMajorIndexBreadthAccess() );
    const hasModelPortfolioAccess = computed< boolean >(() => permissions.value.hasAnyModelPortfolioAccess() );
    const hasNyseBpiAccess = computed< boolean >(() => permissions.value.hasNyseBpiAccess() );
    const hasOptionsCalculatorAccess = computed< boolean >(() => false );
    // const hasOptionsCalculatorAccess = computed< boolean >(() => permissions.value.hasOptionsCalculatorAccess() );
    const hasPortfolioBuilderAccess = computed< boolean >(() => permissions.value.hasPortfolioBuilderAccess() );
    const hasPositionSizeCalculatorAccess = computed< boolean >(() => permissions.value.hasPositionSizeCalculatorAccess() );
    const hasStockReplacementStrategyCalculatorAccess = computed< boolean >(() => permissions.value.hasStockReplacementStrategyCalculatorAccess() );
    const hasProductTradeAlertsAccess = computed< boolean >(() => permissions.value.hasProductTradeAlertsAccess() );
    const hasProfileAccess = computed< boolean >(() => permissions.value.hasProfileAccess() );
    const hasRelativeStrengthMatrixAccess = computed< boolean >(() => permissions.value.hasRelativeStrengthMatrixAccess() );
    const hasScannerAccess = computed< boolean >(() => permissions.value.hasScannerAccess() );

    const hasSectorAlertsAccess = computed< boolean >(() => permissions.value.hasSectorAlertsAccess() );

    const hasSectorAccess = computed< boolean >(() => permissions.value.hasSectorAccess() );

    const hasSectorBpiAccess = computed< boolean >(() => permissions.value.hasSectorBpiAccess() );
    const hasSectorBreadthAccess = computed< boolean >(() => permissions.value.hasSectorBreadthAccess() );
    const hasSpreadCalculatorAccess = computed< boolean >(() => permissions.value.hasSpreadCalculatorAccess() );

    // Build the composite value from any of the criteria that would allow for the Tools menu to be displayed
    const hasToolsAccess = computed< boolean >( () =>
        // Portfolio Tools
        hasPortfolioBuilderAccess.value ||
        hasEquityCalculatorAccess.value ||
        hasOptionsCalculatorAccess.value ||
        hasPositionSizeCalculatorAccess.value ||
        hasStockReplacementStrategyCalculatorAccess.value ||
        hasSpreadCalculatorAccess.value ||
        // Indicators
        hasSectorBpiAccess.value ||
        hasNyseBpiAccess.value ||
        hasRelativeStrengthMatrixAccess.value ||
        hasMajorIndexBreadthAccess.value ||
        // Reference Tools
        hasEtfListingAccess.value ||
        // First to short-circuit the need to test others (Sector Tree & Sectors)
        hasSectorAccess.value );

    const hasWebinarAccess = computed< boolean >( () =>  permissions.value.hasWebinarAccess() );

    // Legacy???
    const hasMyLibraryAccess = hasArticleAccess || hasWebinarAccess || hasBonusReportAccess;

    return {
        // STATE
        isInitialized,

        // ACTIONS
        authorizationGrants,
        clearHealCooldown,
        ensureInitializedAsync,
        initializeAsync,
        initializeSynchronously,
        hasAccessBasedOnShortCode,
        hasAccessBasedOnAnyShortCode,
        hasArticleAccessForProduct,
        hasBonusReportAccessForProduct,
        hasCourseAccessForProduct,
        hasFeatureAccess,
        hasModelPortfolioPermissionsForProduct,
        hasWebinarAccessForProduct,
        reset,

        // GETTERS
        accessibleModelPortfoliosSlugs,
        hasArticleAccess,
        hasBonusReportAccess,
        hasCourseAccess,
        hasEtfAccess,
        hasStockAccess,
        hasEtfListingAccess,
        hasEquityCalculatorAccess,
        hasMainSearchAccess,
        hasMajorIndexBreadthAccess,
        hasModelPortfolioAccess,
        hasNyseBpiAccess,
        hasOptionsCalculatorAccess,
        hasPortfolioBuilderAccess,
        hasPositionSizeCalculatorAccess,
        hasStockReplacementStrategyCalculatorAccess,
        hasProductTradeAlertsAccess,
        hasProfileAccess,
        hasRelativeStrengthMatrixAccess,
        hasScannerAccess,
        hasSectorAlertsAccess,
        hasSectorAccess,
        hasSectorBpiAccess,
        hasSectorBreadthAccess,
        hasSpreadCalculatorAccess,
        hasTradeOfTheDayAccess,
        hasToolsAccess,
        hasWebinarAccess,
        hasMyLibraryAccess,
        isAllianceMember
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( usePermissionsStore, import.meta.hot ) );
}
